<script>
	import { preventDefault } from 'svelte/legacy';

	import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
	import { styleState } from "@app/store/styleStore.svelte.js";
	import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
	import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
	import { projectState } from "@app/store/projectStore.svelte.js";
	import { toast } from '@zerodevx/svelte-toast';
	import { fade } from "svelte/transition";

	/* CC LIB */
	import defaults from "@cclib/lib/defaults.js";
	import encodings from "@cclib/dict/encodings.js";
	import formats from "@cclib/lib/formats.js";
	import frameRates from "@cclib/dict/frameRates.js";
	import _EventGroup from "@cclib/classes/eventGroup.js";
	import LanguageSelect from "@app/components/inputs/LanguageSelect.svelte";

	import tcLib from "@cclib/lib/timecode.js";
	import Swal from "sweetalert2";
	import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import * as m from "@app/paraglide/messages.js";

	import {
		getProjectClockIncode,
		isDropFrameEnabled,
		getUseClockTimeDefault,
		readSubtitleFileContents,
		buildDecodeOptions,
		decodeSubtitleSource,
		detectAutomaticOffset,
		applyAutomaticOffset,
		stripBlankEvents,
		hasOverlaps,
		applyOverlapFix,
		importSpeakersFromGroup,
		applyDropFrameConvert,
	} from "@app/lib/subtitleDecodePipeline.js";

	let eventGroupDefaults = JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
	let subtitleImportDefaults = JSON.parse(localStorage.getItem("cc-subtitle-import-defaults")) || {};

	/* Default Settings */
	let srcFileName,
		srcExt = $state(),
		srcProfile = $state(),
		srcFrameRate = $state(projectState.frameRate),
		srcDropFrame = $state(projectState.dropFrame ? "true" : "false"),
		srcIncode = $state(subtitleImportDefaults.srcIncode || getProjectClockIncode()),
		srcUseClockTime = $state(subtitleImportDefaults.srcUseClockTime || "yes"),
		dropFrameConvert = $state(false),
		showOptions = $state(false),
		files = $state(),
		fileEncoding = $state(defaults.encoding),
		fileContents = $state(""),
		allowedExtensions = "." + formats.srcExt.join(", ."),
		eventGroup = $state("new"),
		importOption = $state(subtitleImportDefaults.importOption || "merge"),
		importTrack = $state(""),
		language = $state(eventGroupDefaults.language || "en-US"),
		rtl = $state(eventGroupDefaults.rtl);

	const TRACK_LETTERS = ["A", "B", "C", "D"];

	function isSrcDropFrameEnabled() {
		return isDropFrameEnabled(srcDropFrame);
	}

	function validateSrcIncode(e) {
		srcIncode = tcLib.tcValidate(
			e.target.value,
			parseFloat(srcFrameRate),
			isSrcDropFrameEnabled(),
		);
	}

	function updateSrcUseClockTime() {
		srcUseClockTime = getUseClockTimeDefault(srcProfile);
	}

	// Handler for file inputs
	function handleFileInput(event) {
		files = Array.from(event.currentTarget.files || []);
		updateFile();
	}

	function updateFile() {
		if (files[0] && files[0].name) {
			let fileName = files[0].name.split(".");
			srcExt = fileName.pop().toLowerCase();
			srcFileName = fileName.shift();
			srcProfile = formats.srcProfiles[srcExt][0];
			updateSrcUseClockTime();
			updateFileContents();
		}
	}

	async function updateFileContents() {
		if (files && files[0]) {
			toast.push(m.subtitle_import_reading_file_toast(), {
                classes: ["toast-warning"],
            });

			try {
				fileContents = await readSubtitleFileContents(files[0], {
					srcExt: srcExt,
					srcProfile: srcProfile,
					fileEncoding: fileEncoding,
				});
			} catch (err) {
				console.log(err, err.message);
				toast.push(
					m.subtitle_import_failed_toast({ error: err.message }),
					{ classes: ["toast-danger"] },
				);
			}
		}
	}

	async function importSubtitleFile() {
		toast.push(m.subtitle_import_importing_toast(), {
            classes: ["toast-warning"],
        });

		try {
			/* Create or select the event group */
			if (eventGroup === "new") {
				let eventGroupOptions = {
					type: "subtitle",
					name: srcFileName,
					language: language,
					rtl: rtl,
                    maxLines : eventGroupDefaults.maxLines || 2,
                    maxChars : eventGroupDefaults.maxChars || 32,
                    minDuration : eventGroupDefaults.minDuration || 0.5,
                    maxDuration : eventGroupDefaults.maxDuration || 12,
                    overlap : eventGroupDefaults.overlap,
                    fontFamily: eventGroupDefaults.fontFamily ?? "plexMono",
                    fontSize: eventGroupDefaults.fontSize || 16
				};
                
				eventGroupState.push({...(new _EventGroup(eventGroupOptions))});
				projectState.selected = eventGroupState.length - 1;
			} else {
				projectState.selected = eventGroupState.findIndex((group) => {
					return group.id === eventGroup;
				});
			}

			srcDropFrame = isSrcDropFrameEnabled();
			srcIncode = tcLib.tcValidate(
				srcIncode,
				parseFloat(srcFrameRate),
				srcDropFrame,
			);

			let options = buildDecodeOptions({
				srcProfile: srcProfile,
				srcFrameRate: srcFrameRate,
				srcDropFrame: srcDropFrame,
				srcIncode: srcIncode,
				srcUseClockTime: srcUseClockTime,
				formatOptions: formats.profileMapping[srcProfile].options.decode,
				window: {
                    width: styleState.width,
                    height: styleState.height,
                    xPadding: styleState.xPadding,
                    yPadding: styleState.yPadding
                }
			});

			//console.log("Options:", JSON.stringify(options, null, 4));
			let decodedEvGroup = await decodeSubtitleSource(fileContents, options);
			let offsetAmount = detectAutomaticOffset(decodedEvGroup);

			if (offsetAmount != 0) {
				try {
					if (projectState.incode > 0){
						decodedEvGroup = applyAutomaticOffset(decodedEvGroup);
					} else {
						let offsetRes = await Swal.fire({
							...SWAL_BASE,
							customClass: SWAL_CLASSES.confirmCancelLight,
							title: m.subtitle_import_auto_offset_title(),
							text: m.subtitle_import_auto_offset_text({
                                timecode: tcLib.secToTcClock(
                                    offsetAmount,
                                    projectState.frameRate,
                                    projectState.dropFrame
                                ),
                            }),
							icon: "question",
							showCancelButton: true,
							confirmButtonText: m.yes(),
							cancelButtonText: m.no(),
							allowOutsideClick: false,
							allowEscapeKey: false,
						});

						if (offsetRes.isConfirmed) {
							projectState.incode = offsetAmount;
							decodedEvGroup = applyAutomaticOffset(decodedEvGroup);
						}
					}

				} catch (err) {
					console.log(err, err.message);
				}
			}

			decodedEvGroup = stripBlankEvents(decodedEvGroup);

			//Check for overlaps in the new event group. If overlaps are found, ask the user if they want them fixed using SweetAlert2
			if (hasOverlaps(decodedEvGroup)){
				let overlapRes = await Swal.fire({
					...SWAL_BASE,
					customClass: SWAL_CLASSES.confirmCancelLight,
					title: m.subtitle_import_overlap_title(),
					text: m.subtitle_import_overlap_text(),
					icon: "question",
					showCancelButton: true,
					confirmButtonText: m.yes(),
					cancelButtonText: m.no(),
					allowOutsideClick: false,
					allowEscapeKey: false,
				});

				if (overlapRes.isConfirmed) {
					decodedEvGroup = applyOverlapFix(decodedEvGroup);
				}
			}

            //Import Speakers from events
            importSpeakersFromGroup(decodedEvGroup);

			if (dropFrameConvert) {
				decodedEvGroup = applyDropFrameConvert(decodedEvGroup);
			}

			if (eventGroup === "new" || importOption === "replace") {
				eventGroupState[projectState.selected].events = decodedEvGroup.events;
			} else {
				//Optionally assign imported events to a track when merging with an existing group
				if (TRACK_LETTERS.includes(importTrack)) {
					decodedEvGroup.events.forEach((event) => {
						event.track = importTrack;
					});
				}

				eventGroupState[projectState.selected].events = [...eventGroupState[projectState.selected].events, ...decodedEvGroup.events];
			}

			insertHistoryAction({
				name: "import subtitles", //action name
				eventGroup: projectState.selected,
				snapshots: [
					{
						store: "eventGroupState",
						value: JSON.stringify(eventGroupState),
					},
				],
			});

			localStorage.setItem(
				"cc-subtitle-import-defaults",
				JSON.stringify({
					showOptions: showOptions,
					importOption: importOption,
					srcIncode: srcIncode,
					srcUseClockTime: srcUseClockTime,
				})
			);

			hideModal();
		} catch (err) {
			console.log(err, err.message);
			toast.push(
                m.subtitle_import_failed_toast({ error: err.message }),
                { classes: ["toast-danger"] },
            );
		}
	}
</script>

<div transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'subtitleImport' ? 'show d-block' : ''}" 
    role="dialog"
    aria-labelledby="subtitleImportTitle"
    aria-modal="true" 
    tabindex="-1" 
    id="SubtitleImportModal">
	<div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
		<div class="modal-content">
			<div class="modal-header">
				<h4 class="modal-title" id="subtitleImportTitle">
                    {m.subtitle_import_title()}
                </h4>
				<button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal}></button>
			</div>
			<div class="modal-body">
				<p class="small text-muted"><i class="bi bi-lightning-fill"></i> {m.subtitle_import_powered_by()}</p>

				<div class="row">
					<div class="col">
						<!-- Import Form Start -->
						<form onsubmit={preventDefault(importSubtitleFile)} aria-label={m.subtitle_import_form_label()}>
							<!-- File Input -->
							<div class="mb-3 shadow bg-secondary rounded p-3">
								<label class="form-label" id="fileInputLabel" for="subtitleFilePicker">{m.subtitle_import_select_file_label()} <i class="bi bi-info-circle" aria-label={m.subtitle_import_supported_formats_label({ formats: allowedExtensions })}></i></label>
								<input type="file" class="form-control" id="subtitleFilePicker" onchange={handleFileInput} accept={allowedExtensions} aria-labelledby="fileInputLabel" />
							</div>
							<div class="row">
								<!-- Target Event Group Option Start -->
								<div class="mb-3 col-6">
									<label class="form-label text-warning" for="EventGroupSelection">{m.subtitle_import_event_group_label()}</label>
									<select class="form-select" bind:value={eventGroup}>
										{#each eventGroupState as eventGroupOption (eventGroupOption.id)}
											<option value={eventGroupOption.id}>{eventGroupOption.name}</option>
										{/each}
										<option class="fw-bold" value="new">{m.new_badge()}</option>
									</select>
								</div>
								<!-- Target Event Group Option End -->
								<!-- Merge or Replace Option Start -->
								<!-- {#if eventGroup !== "new"} -->
									<div class="mb-3 col-6">
										<p class="text-warning">{m.subtitle_import_merge_replace_label()} <i class="bi bi-info-circle" title={m.subtitle_import_merge_replace_help()}></i></p>

										<div class="form-check">
											<input class="form-check-input" disabled={eventGroup === 'new'} type="radio" bind:group={importOption} name="importOption" id="mergeImportOption" value="merge" checked />
											<label class="form-check-label" for="mergeImportOption"> {m.subtitle_import_merge_label()} </label>
										</div>
										<div class="form-check">
											<input class="form-check-input" disabled={eventGroup === 'new'} type="radio" bind:group={importOption} name="importOption" id="replaceImportOption" value="replace" />
											<label class="form-check-label" for="replaceImportOption"> {m.subtitle_import_replace_label()} </label>
										</div>
										<!-- Optional Track Assignment (merge only) -->
										<div class="mt-2">
											<label class="form-label" for="importTrackSelect">{m.event_track_import_label()} <i class="bi bi-info-circle" title={m.event_track_import_help()}></i></label>
											<select id="importTrackSelect" class="form-select form-select-sm" bind:value={importTrack} disabled={eventGroup === 'new' || importOption !== 'merge'}>
												<option value="">{m.event_track_none()}</option>
												{#each TRACK_LETTERS as letter (letter)}
													<option value={letter}>{m.multiview_track()} {letter}</option>
												{/each}
											</select>
										</div>
									</div>
                                    <!-- Language -->
									<div class="mb-3 col-12">
										<label class="form-label text-warning" for="Language">{m.subtitle_import_group_language_label()}</label>
										<LanguageSelect
											id="Language"
											class="form-control"
											bind:value={language}
											disabled={eventGroup !== 'new'} />
									</div>
									<div class="mb-3 col-12">
										<div class="form-check form-switch mb-2">
											<input type="checkbox" class="form-check-input" id="rtlSwitch" bind:checked={rtl} disabled={eventGroup !== 'new'}/>
											<label class="form-check-label" for="rtlSwitch">{m.right_to_left()}</label>
										</div>
									</div>
								<!-- Merge or Replace Option End -->
							</div>
							<!-- Source Profile List -->
							{#if formats.srcProfiles[srcExt] && formats.srcProfiles[srcExt].length > 1}
								<div class="mb-3">
									<label class="form-label" for="srcProfileSelectList">{m.subtitle_import_source_profile_label()} <i class="bi bi-asterisk text-danger"></i></label>
									<select id="srcProfileSelectList" class="form-select" bind:value={srcProfile} onchange={() => {
										updateSrcUseClockTime();
										updateFileContents();
									}}>
										{#each formats.srcProfiles[srcExt] as profile (profile)}
											<option>{profile}</option>
										{/each}
									</select>
								</div>
							{/if}
							<button
								type="button"
								class="btn btn-outline-warning mb-2 dropdown-toggle"
								onclick={() => {
									showOptions = !showOptions;
								}}>{m.more_options()}</button>
							{#if showOptions}
								<div id="advancedOptions" class="p-3 bg-secondary shadow rounded">
									<div class="row pt-2 mb-2">
										<h5 class="col-12 lead text-warning">{m.subtitle_import_general_options()}</h5>
									</div>
									
                                    <div class="row mb-2">
                                        <div class="mb-3 col-8">
											<label class="form-label" for="srcIncode">{m.subtitle_export_incode_label()}</label>
                                            <input
												type="text"
												class="form-control"
												id="srcIncode"
												bind:value={srcIncode}
												onblur={validateSrcIncode}
												onfocus={(e) => {
													e.target.select();
												}} />
                                        </div>
                                        <div class="mb-3 col-4">
											<label class="form-label" for="srcUseClockTime">{m.subtitle_export_use_clock_time()}</label>
                                            <select id="srcUseClockTime" class="form-select" bind:value={srcUseClockTime}>
												<option value="yes">{m.subtitle_export_yes()}</option>
												<option value="no">{m.subtitle_export_no()}</option>
                                            </select>
                                        </div>
                                        <div class="col-8">
											<label class="form-label" for="SrcFrameRateSelect">{m.subtitle_import_source_frame_rate_label()}</label>
                                            <select id="SrcFrameRateSelect" class="form-select" bind:value={srcFrameRate}>
                                                {#each frameRates.frameRates as frameRate (frameRate)}
                                                    <option>{frameRate}</option>
                                                {/each}
                                            </select>
                                        </div>
                                        {#if parseFloat(srcFrameRate) === 29.97 || parseFloat(srcFrameRate) === 59.94}
                                            <div class="col-4">
												<label class="form-label" for="SrcDropFrameSelect">{m.subtitle_import_drop_frame_label()}</label>
                                                <select id="SrcDropFrameSelect" class="form-select" bind:value={srcDropFrame}>
													<option value="true">{m.common_true()}</option>
													<option value="false">{m.common_false()}</option>
                                                </select>
                                            </div>
                                        {/if}
                                    </div>
									<div class="mb-3 mt-2">
										<label class="form-label" for="srcFileEncoding">{m.subtitle_import_file_encoding_label()}</label>
										<select id="srcFileEncoding" class="form-select" bind:value={fileEncoding} onchange={updateFileContents}>
											{#each encodings as encodingOption (encodingOption)}
												<option>{encodingOption}</option>
											{/each}
										</select>
									</div>
									<div class="form-check">
										<input class="form-check-input" type="checkbox" id="dropFrameConvert" bind:checked={dropFrameConvert} />
										<label class="form-check-label" for="dropFrameConvert">
											{m.subtitle_import_drop_frame_convert_label()}
											<i class="bi bi-info-circle" title={m.subtitle_import_drop_frame_convert_help()}></i>
										</label>
									</div>
									{#if srcProfile && formats.profileMapping[srcProfile].options && formats.profileMapping[srcProfile].options.decode.length > 0}
										<div class="row pt-4 mt-4 mb-3 border-top border-secondary">
											<h5 class="col-12 lead text-warning">{m.subtitle_import_decoding_options_label()}</h5>
											{#each formats.profileMapping[srcProfile].options.decode as decodeOption (decodeOption.name)}
												<div class="mb-3 col-6">
													<label class="form-label" for="decodingOption">{decodeOption.name}</label>
													{#if decodeOption.type === "textarea"}
														<textarea class="form-control" rows="3" bind:value={decodeOption.selected}></textarea>
													{:else if decodeOption.type === "text-input"}
														<input class="form-control" type="text" bind:value={decodeOption.selected} />
													{:else if decodeOption.type === "number-input"}
														<input class="form-control" type="number" bind:value={decodeOption.selected} />
													{:else}
														<select class="form-select" bind:value={decodeOption.selected}>
															{#each decodeOption.values as valueOption (valueOption)}
																<option>{valueOption}</option>
															{/each}
														</select>
													{/if}
												</div>
											{/each}
										</div>
									{/if}
								</div>
								<!-- End of Advanced Settings -->
							{/if}
						</form>
						<!-- Import Form End -->
					</div>
					<div class="col">
						<!-- File Preview -->
						<form id="SourcePreviewForm">
							<div class="mb-3">
								<label class="form-label" for="srcPreviewText">{m.subtitle_import_source_preview_label()}</label>
								<textarea bind:value={fileContents} disabled class="form-control" id="srcPreviewText" rows="10" placeholder={m.subtitle_import_source_preview_placeholder()}></textarea>
							</div>
						</form>
					</div>
				</div>
			</div>
			<div class="modal-footer">
				<button class="btn btn-primary" type="button" onclick={importSubtitleFile} disabled={fileContents && fileContents.length === 0} aria-label={m.subtitle_import_action_label()}>{m.subtitle_import_action_label()} </button>
			</div>
		</div>
	</div>
</div>

<style lang="css">
	#advancedOptions {
		overflow-y: auto;
		overflow-x: none;
		max-height: 50vh;
	}
</style>
